import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { promptAPI } from '@/lib/api';
import { Sparkles, ArrowLeft, Copy, ThumbsUp, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Library = () => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [filter, setFilter] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newPrompt, setNewPrompt] = useState({
    title: '',
    content: '',
    category: 'image',
    ai_tool: 'chatgpt'
  });

  useEffect(() => {
    loadPrompts();
  }, [filter]);

  const loadPrompts = async () => {
    try {
      const data = await promptAPI.getLibrary(filter, 0, 20);
      setPrompts(data.prompts || []);
    } catch (error) {
      console.error('Load error:', error);
    }
  };

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Prompt copied!');
  };

  const handleSavePrompt = async () => {
    if (!newPrompt.title.trim() || !newPrompt.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSaving(true);
    try {
      await promptAPI.save({
        title: newPrompt.title,
        content: newPrompt.content,
        category: newPrompt.category,
        ai_tool: newPrompt.ai_tool,
        is_public: true
      });
      toast.success('Prompt uploaded to library!');
      setIsDialogOpen(false);
      setNewPrompt({ title: '', content: '', category: 'image', ai_tool: 'chatgpt' });
      loadPrompts(); // Reload the library
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to upload prompt');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen">
      <nav className="px-6 py-4 bg-white/80 backdrop-blur-sm border-b-4 border-black sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Sparkles className="h-7 w-7 text-primary" />
          <h1 className="text-xl font-bold">Community Library</h1>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-4xl font-bold text-foreground">Community <span className="text-primary">Library</span></h2>
            <p className="text-lg text-muted-foreground mt-2">Browse and share winning prompts</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-white shadow-md hover:shadow-lg font-semibold">
                <Plus className="mr-2 h-5 w-5" /> Upload Prompt
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Upload Your Prompt</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Title</label>
                  <Input 
                    value={newPrompt.title}
                    onChange={(e) => setNewPrompt({...newPrompt, title: e.target.value})}
                    placeholder="e.g., Epic Dragon Fantasy Scene"
                    className="border border-border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Prompt Content</label>
                  <Textarea 
                    value={newPrompt.content}
                    onChange={(e) => setNewPrompt({...newPrompt, content: e.target.value})}
                    placeholder="Enter your full prompt here..."
                    className="min-h-[150px] border border-border font-mono text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Category</label>
                    <Select value={newPrompt.category} onValueChange={(v) => setNewPrompt({...newPrompt, category: v})}>
                      <SelectTrigger className="border border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="image">Image</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                        <SelectItem value="story">Story</SelectItem>
                        <SelectItem value="ad">Product Ad</SelectItem>
                        <SelectItem value="social">Social Post</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">AI Tool</label>
                    <Select value={newPrompt.ai_tool} onValueChange={(v) => setNewPrompt({...newPrompt, ai_tool: v})}>
                      <SelectTrigger className="border border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="chatgpt">ChatGPT</SelectItem>
                        <SelectItem value="gemini">Gemini</SelectItem>
                        <SelectItem value="claude">Claude</SelectItem>
                        <SelectItem value="midjourney">Midjourney</SelectItem>
                        <SelectItem value="dall-e">DALL-E</SelectItem>
                        <SelectItem value="sora">Sora</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  onClick={handleSavePrompt}
                  disabled={isSaving}
                  className="w-full bg-primary text-white shadow-md hover:shadow-lg font-semibold py-6"
                >
                  {isSaving ? (
                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Uploading...</>
                  ) : (
                    <><Plus className="mr-2 h-5 w-5" /> Upload to Library</>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex gap-3 mb-8 mt-8">
          <Button onClick={() => setFilter(null)} variant={!filter ? 'default' : 'outline'} className="border border-border">All</Button>
          <Button onClick={() => setFilter('image')} variant={filter === 'image' ? 'default' : 'outline'} className="border border-border">Image</Button>
          <Button onClick={() => setFilter('video')} variant={filter === 'video' ? 'default' : 'outline'} className="border border-border">Video</Button>
          <Button onClick={() => setFilter('story')} variant={filter === 'story' ? 'default' : 'outline'} className="border border-border">Story</Button>
          <Button onClick={() => setFilter('ad')} variant={filter === 'ad' ? 'default' : 'outline'} className="border border-border">Ad</Button>
          <Button onClick={() => setFilter('social')} variant={filter === 'social' ? 'default' : 'outline'} className="border border-border">Social</Button>
        </div>

        {prompts.length === 0 ? (
          <Card className="p-12 bg-white border border-border card-shadow-lg text-center">
            <p className="text-lg text-muted-foreground">No prompts yet. Be the first to contribute!</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {prompts.map((prompt) => (
              <Card key={prompt.prompt_id} className="p-6 bg-white border border-border card-shadow-lg hover:shadow-xl transition-all">
                <div className="flex items-start justify-between mb-3">
                  <span className="bg-primary/10 px-3 py-1 rounded-lg text-xs font-semibold border border-primary/20 text-primary">
                    {prompt.category}
                  </span>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <ThumbsUp className="h-4 w-4" />
                    {prompt.upvotes}
                  </div>
                </div>
                <h3 className="font-semibold mb-2 text-foreground">{prompt.title}</h3>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-3 font-mono leading-relaxed">{prompt.content}</p>
                <Button 
                  onClick={() => handleCopy(prompt.content)}
                  size="sm"
                  className="w-full bg-accent text-white shadow-md hover:shadow-lg font-semibold"
                >
                  <Copy className="h-4 w-4 mr-2" /> Copy Prompt
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Library;
